import { CommunicationChannel } from '../CommunicationChannel';

/** Channels whose conversations are owned by something outside Symphony (see resolveExternallyOwnedChannel). */

export const CONVERSATION_CHANNEL_READ_ONLY_ERROR_CODE = 'CONVERSATION_CHANNEL_READ_ONLY';

/**
 * Typed 403 body returned by the client-facing send endpoints when a USER send targets an
 * externally-owned conversation, so clients can render a channel-specific "continue on WhatsApp/SMS"
 * CTA instead of a generic error.
 */

/** The two recipient slots digest-engine's AssistantMessageDeliveryConsumer routes by. */

/**
 * Resolve whether `conversation` is owned by something outside Symphony, so a USER send from one of
 * our own clients must be refused.
 *
 * Two different reasons, one rule:
 *  - **WHATSAPP / SMS** — assistant replies are delivered into the user's REAL messaging thread by
 *    digest-engine's AssistantMessageDeliveryConsumer, so a send from a Symphony client would make the
 *    assistant answer a question that never appeared in that thread.
 *  - **EXTERNAL_AGENT** — the conversation belongs to an external agent platform's session (the first
 *    being Gemini Enterprise over A2A). That platform only ever sees a reply while it is *waiting on a
 *    request it made*; there is no channel to push into afterwards, so a reply typed here would be
 *    written to a transcript nobody on the other side will ever read.
 *
 * For WhatsApp/SMS the recipient's active{Sms,Whatsapp}ConversationId slots are the source of truth
 * (that's exactly how the delivery consumer routes); the conversation's own channel field is unreliable
 * on legacy rows (carries UNKNOWN). EXTERNAL_AGENT needs no such fallback — the channel post-dates the
 * legacy rows, so it is always stamped explicitly and there is no recipient slot to consult.
 *
 * Cheap path first: an explicit externally-owned channel blocks immediately and any other explicit
 * channel (APP/WEB/…) allows immediately — neither invokes `fetchRecipient`. Only the ambiguous
 * UNKNOWN/unset channel pays the recipient lookup. A null recipient (miss or swallowed fetch failure)
 * fails open — the delivery consumer needs that same recipient to route externally.
 */
export async function resolveExternallyOwnedChannel(conversation, fetchRecipient) {
  const channel = conversation.communicationChannel;
  if (channel === CommunicationChannel.WHATSAPP || channel === CommunicationChannel.SMS || channel === CommunicationChannel.EXTERNAL_AGENT) {
    return channel;
  }
  if (channel != null && channel !== CommunicationChannel.UNKNOWN_COMMUNICATION_CHANNEL) {
    return null;
  }
  const recipient = await fetchRecipient();
  if (!recipient || !conversation.id) {
    return null;
  }
  // Same slot precedence as the delivery consumer: SMS first, then WhatsApp.
  if (recipient.activeSmsConversationId === conversation.id) {
    return CommunicationChannel.SMS;
  }
  if (recipient.activeWhatsappConversationId === conversation.id) {
    return CommunicationChannel.WHATSAPP;
  }
  return null;
}
//# sourceMappingURL=channelOwnership.js.map